import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { IconName } from '@const/icons.enum';
import { UserRole } from '@const/users.enum';
import { AuthService } from '@service/auth/auth.service';
import { UserService } from '@service/auth/user.service';
import { LoadingVerificationService } from '@service/loading-verification/loading-verification.service';
import { ReportService } from '@service/report/report.service';
import { ResponsiveService } from '../../services/responsive/responsive.service';
import { Subject, firstValueFrom } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { NAVIGATION_CONFIG } from './config/navigation.config';
import { MenuItem, SubMenuItem } from './interface/navigation.interface';
import { filterMenuByRoles, toggleSingleMenuExpansion } from './utils/navigation.utils';

/** Route of the submenu entry gated by the loading verification beta cohort (CYLP-542). */
const LOADING_VERIFICATION_ROUTE = '/operating/loading-verification';

/**
 * Left Navigation Component with Role-Based Access Control
 *
 * This component uses the navigation configuration from navigation.config.ts
 * to display menu items based on user roles.
 *
 * Features:
 * - Role-based menu filtering
 * - Single menu expansion (only one submenu can be open at a time)
 * - Smart expansion: clicking when collapsed expands navigation and submenu
 * - Auto-collapse after navigation (for items without submenus)
 * - Immediate collapse when clicking items without submenus
 *
 * Behavior:
 * - When collapsed: First click expands navigation (+ submenu if applicable)
 * - When expanded: Click toggles submenu or navigates and collapses
 *
 * The menu items and their role permissions are now centralized in the config file
 * for easy maintenance and updates.
 */
@Component({
  selector: 'app-left-navigation',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatListModule,
    MatSidenavModule,
    MatButtonModule,
    MatTooltipModule,
    RouterModule,
    TranslateModule,
  ],
  templateUrl: './left-navigation.html',
  styleUrls: ['./left-navigation.scss'],
})
export class LeftNavigationComponent implements OnInit, OnDestroy {
  @Input() disabled = false; // Add disabled input
  @Input() mobileMenuOpen = false; // Mobile menu open state from parent
  @Output() toggleChange = new EventEmitter<boolean>();
  @Output() mobileMenuStateChange = new EventEmitter<boolean>();

  private authService = inject(AuthService);
  private userService = inject(UserService);
  private reportService = inject(ReportService);
  private loadingVerificationService = inject(LoadingVerificationService);
  protected responsiveService = inject(ResponsiveService);
  private destroy$ = new Subject<void>();

  constructor(private router: Router) {}

  isCollapsed = true; // Start collapsed as initial state
  userRoles: UserRole[] = [];
  showToggleButton = true; // Control toggle button visibility
  isDisablePage = false; // Track if we're on disable page

  // Filtered menu items based on user roles (loaded from config)
  menuItems: MenuItem[] = [];

  // Make IconName available in template
  IconName = IconName;

  async ngOnInit() {
    // Only load user roles if authenticated - prevents retry loops during redirect to login
    const isAuthenticated = await this.authService.isAuthenticated();
    if (isAuthenticated) {
      await this.loadUserRolesAndMenu();
    }
  }

  private async loadUserRolesAndMenu(retryCount = 0): Promise<void> {
    try {
      this.userRoles = await this.authService.getUserRoles();
      let menuItems = filterMenuByRoles(NAVIGATION_CONFIG, this.userRoles);

      menuItems = await this.applyLoadingVerificationBeta(menuItems);

      // Check if user has report download access (for non-admin/material-admin users)
      const hasReportRole = this.userRoles.some(
        (role) => role === UserRole.ADMIN || role === UserRole.MATERIAL_ADMIN,
      );

      if (!hasReportRole) {
        // Check if user is in report access list
        try {
          // Use the profile CWID, NOT the JWT custom:cwid claim - external users
          // present their Entra mailnickname ("name_company.com#EXT#") in the claim
          // while the allowlist holds their real profile CWID.
          const userProfile = await firstValueFrom(this.userService.getCurrentUser());
          const userCwid = userProfile?.cwid ?? '';
          const reportAccessConfig = await firstValueFrom(
            this.reportService.getReportAccessConfig(),
          );

          const canAccessChinaList = reportAccessConfig.chinaLoadingListUsers.some(
            (cwid) => cwid.toLowerCase() === userCwid.toLowerCase(),
          );
          const canAccessBasicChemical = reportAccessConfig.dataExportUsers.some(
            (cwid) => cwid.toLowerCase() === userCwid.toLowerCase(),
          );

          // If user has any report access, add the Report menu with only Downloads
          if (canAccessChinaList || canAccessBasicChemical) {
            const reportMenuItem: MenuItem = {
              name: 'nav.report',
              icon: IconName.NAV_PILLAR_CHART_INCREASE,
              active: false,
              hasSubmenu: true,
              expanded: false,
              route: '/report',
              allowedRoles: [UserRole.ADMIN],
              submenu: [
                {
                  name: 'nav.downloads',
                  active: false,
                  route: '/report/downloads',
                  allowedRoles: [UserRole.ADMIN],
                },
              ],
            };
            menuItems = [...menuItems, reportMenuItem];
          }
        } catch (error) {
          console.error('Error checking report access:', error);
        }
      }

      this.menuItems = menuItems;
    } catch (error) {
      // Retry up to 3 times with delay if tokens aren't ready yet
      // But first check if still authenticated to avoid retry loops
      const stillAuthenticated = await this.authService.isAuthenticated();
      if (stillAuthenticated && retryCount < 3) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return this.loadUserRolesAndMenu(retryCount + 1);
      }
      console.error('Error loading user roles for navigation:', error);
      this.menuItems = [];
    }

    // Listen to route changes to hide toggle button and disable menu on disable page
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        this.isDisablePage = event.url.includes('/profile/disable');
        this.showToggleButton = !this.isDisablePage;
      });

    // Auto-close mobile menu when switching to desktop
    this.responsiveService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop) => {
      if (isDesktop && this.mobileMenuOpen) {
        this.closeMobileMenu();
      }
    });
  }

  /**
   * Drop the Loading Verification entry unless the user is in the beta cohort (CYLP-542).
   *
   * The item's role set is wider than the pilot, so this cannot be expressed as roles. Admins
   * keep it without being listed, matching the backend and the route guard.
   *
   * A parent left with an empty submenu is dropped too: Operational Admin is admitted to
   * Operating *only* so this item is reachable, so for a non-pilot one the whole Operating
   * entry would otherwise sit there opening onto nothing.
   *
   * On any error the item is hidden. A menu that cannot find out whether you are in the pilot
   * should not offer a page that will refuse you.
   */
  private async applyLoadingVerificationBeta(menuItems: MenuItem[]): Promise<MenuItem[]> {
    const isBetaItem = (subItem: SubMenuItem) => subItem.route === LOADING_VERIFICATION_ROUTE;

    if (
      !menuItems.some((item) => item.submenu?.some(isBetaItem)) ||
      this.userRoles.includes(UserRole.ADMIN)
    ) {
      return menuItems;
    }

    let inCohort = false;

    try {
      // Profile CWID, not the JWT custom:cwid claim - see loading-verification-beta.guard.ts
      const userProfile = await firstValueFrom(this.userService.getCurrentUser());
      const userCwid = (userProfile?.cwid ?? '').toLowerCase();
      const betaUsers = await firstValueFrom(this.loadingVerificationService.getBetaUsers());

      inCohort = !!userCwid && betaUsers.some((cwid) => cwid.toLowerCase() === userCwid);
    } catch (error) {
      console.error('Error checking loading verification beta access:', error);
    }

    if (inCohort) {
      return menuItems;
    }

    return menuItems
      .map((item) => ({
        ...item,
        submenu: item.submenu?.filter((subItem) => !isBetaItem(subItem)),
      }))
      .filter((item) => !item.hasSubmenu || (item.submenu?.length ?? 0) > 0);
  }

  /**
   * Opens the mobile menu
   */
  openMobileMenu(): void {
    if (this.disabled || this.isDisablePage) return;
    this.mobileMenuStateChange.emit(true);
  }

  /**
   * Closes the mobile menu
   */
  closeMobileMenu(): void {
    this.mobileMenuStateChange.emit(false);
  }

  /**
   * Toggles the mobile menu open/closed state
   */
  toggleMobileMenu(): void {
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  /**
   * Check if we're on mobile/tablet
   */
  get isMobileOrTablet(): boolean {
    return this.responsiveService.isMobile || this.responsiveService.isTablet;
  }

  /**
   * Determines if expanded content (text labels, submenus) should be shown.
   * On mobile/tablet: show expanded content when mobile menu is open
   * On desktop: show expanded content when sidebar is not collapsed
   */
  get showExpandedContent(): boolean {
    if (this.isMobileOrTablet) {
      return this.mobileMenuOpen;
    }
    return !this.isCollapsed;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidebar() {
    if (this.disabled) return; // Don't toggle if disabled
    this.isCollapsed = !this.isCollapsed;
    this.toggleChange.emit(this.isCollapsed);
  }

  /**
   * Handle toggle button click - works for both desktop and mobile
   * On mobile: Opens/closes the mobile menu
   * On desktop: Expands/collapses the sidebar
   */
  onToggleButtonClick(): void {
    if (this.disabled || this.isDisablePage) return;

    if (this.isMobileOrTablet) {
      this.toggleMobileMenu();
    } else {
      this.toggleSidebar();
    }
  }

  onMenuItemClick(item: MenuItem) {
    if (this.disabled || this.isDisablePage) return; // Don't handle clicks if disabled or on disable page

    // If navigation is collapsed, expand it first
    if (this.isCollapsed) {
      this.isCollapsed = false;
      this.toggleChange.emit(this.isCollapsed);

      // If item has submenu, expand it; otherwise just navigate
      if (item.hasSubmenu) {
        // Close all other expanded items and expand this one
        this.menuItems.forEach((menuItem: MenuItem) => {
          if (menuItem !== item) {
            menuItem.expanded = false;
          }
        });
        item.expanded = true;
      } else {
        // Set active state and navigate
        this.menuItems.forEach((menuItem: MenuItem) => (menuItem.active = false));
        item.active = true;
        if (item.route) {
          this.router.navigate([item.route]);
        }
      }
    } else if (item.hasSubmenu) {
      // Navigation is already expanded, toggle submenu
      toggleSingleMenuExpansion(this.menuItems, item);
    } else {
      // Navigation is expanded, item has no submenu - navigate and collapse
      this.menuItems.forEach((menuItem: MenuItem) => (menuItem.active = false));
      item.active = true;

      if (item.route) {
        this.router.navigate([item.route]);
      }

      // Collapse the navigation immediately
      this.isCollapsed = true;
      this.toggleChange.emit(this.isCollapsed);

      // Close mobile menu if open
      if (this.mobileMenuOpen) {
        this.closeMobileMenu();
      }
    }
  }

  onSubmenuItemClick(subitem: SubMenuItem) {
    if (this.disabled || this.isDisablePage) return; // Don't handle clicks if disabled or on disable page

    // Set active state for submenu items
    this.menuItems.forEach((menuItem: MenuItem) => {
      menuItem.active = false;
      if (menuItem.submenu) {
        menuItem.submenu.forEach((sub: SubMenuItem) => (sub.active = false));
      }
    });
    subitem.active = true;

    // Navigate to the submenu route
    if (subitem.route) {
      this.router.navigate([subitem.route]);

      // Collapse the navigation after routing
      this.isCollapsed = true;
      this.toggleChange.emit(this.isCollapsed);

      // Close mobile menu if open
      if (this.mobileMenuOpen) {
        this.closeMobileMenu();
      }
    }
  }

  onLogoClick() {
    if (this.disabled || this.isDisablePage) return;
    this.router.navigate(['/profile']);

    // Close mobile menu if open
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  onSupportClick() {
    if (this.disabled || this.isDisablePage) return;
    this.router.navigate(['/help-support']);
    if (!this.isCollapsed) {
      this.isCollapsed = true;
      this.toggleChange.emit(this.isCollapsed);
    }

    // Close mobile menu if open
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    }
  }
}
